import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

export const supabase = isSupabaseConfigured
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

const DEFAULT_SITE_CONFIG = {
  email: 'record@swaarnimbookofrecords.com',
  whatsapp: '9239755599',
  about_content: [
    'Swaarnim Book Of Records is a distinguished platform dedicated to recognizing and documenting exceptional talents and achievements.',
    'Every participant receives an official certificate of recognition for their unique accomplishment.',
    'Our mission is to inspire excellence through a transparent and credible platform for remarkable achievements.',
  ],
  hero_title: 'WELCOME TO',
  hero_subtitle: 'Swaarnim Book Of Records',
  hero_description: 'Swaarnim Book Of Records is a premier skill registration and achievement recognition platform.',
};

const requireSupabase = () => {
  if (!supabase) {
    throw new Error('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
  }
  return supabase;
};

const assertOk = ({ error }) => {
  if (error) throw error;
};

const parseErrorMessage = (error) => {
  if (!error) return 'Please try again.';
  if (typeof error === 'string') return error;
  if (error.message && typeof error.message === 'string') return error.message;
  if (error.error_description) return error.error_description;
  if (error.details && typeof error.details === 'string') return error.details;
  return 'Please try again.';
};

const isBodyStreamReadError = (error) =>
  parseErrorMessage(error).toLowerCase().includes('body stream already read');

const arraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const siteConfigMatchesPayload = (config, payload) =>
  Boolean(config) &&
  config.email === payload.email &&
  config.whatsapp === payload.whatsapp &&
  arraysEqual(config.about_content ?? [], payload.about_content ?? []) &&
  config.hero_title === payload.hero_title &&
  config.hero_subtitle === payload.hero_subtitle &&
  config.hero_description === payload.hero_description;

const describeCurrentSession = async () => {
  if (!supabase) return 'Supabase is not configured.';
  try {
    const { data } = await supabase.auth.getSession();
    const user = data?.session?.user;
    if (!user?.email) return 'No active admin session was found.';
    const provider = user.app_metadata?.provider || user.app_metadata?.providers?.join(', ') || 'unknown provider';
    return `Signed in as ${user.email} via ${provider}.`;
  } catch {
    return 'The active admin session could not be checked.';
  }
};

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `post-${Date.now()}`;

const normalizeFaq = (faq) => ({
  ...faq,
  order: faq.display_order ?? faq.order ?? 0,
  published: faq.published ?? true,
});

const normalizeBlogPost = (post) => ({
  ...post,
  content: post.body ?? post.content ?? '',
  image: post.cover_image ?? post.image ?? '',
  published: post.published ?? true,
});

const normalizeRecordHolder = (holder) => ({
  ...holder,
  images: holder.images ?? [],
  certificate_image: holder.certificate_image ?? '',
  published: holder.published ?? true,
});

const normalizeApplication = async (application) => {
  const attachmentPaths = application.attachment_paths ?? [];
  const attachmentNames = application.attachment_names ?? [];
  if (attachmentPaths.length === 0) {
    return { ...application, attachment_paths: [], attachments: [] };
  }

  const attachments = await Promise.all(
    attachmentPaths.map(async (path, index) => {
      const { data, error } = await requireSupabase()
        .storage
        .from('application-attachments')
        .createSignedUrl(path, 60 * 60);
      return {
        path,
        name: attachmentNames[index] || path.split('/').pop() || 'Attachment',
        url: error ? '' : data?.signedUrl || '',
      };
    })
  );

  return { ...application, attachment_paths: attachmentPaths, attachment_names: attachmentNames, attachments };
};

const buildPublicUrl = (bucket, path) => {
  const { data } = requireSupabase().storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

const storagePathFromPublicUrl = (url, bucket) => {
  if (!url || !SUPABASE_URL) return null;
  const marker = `/storage/v1/object/public/${bucket}/`;
  const [, path] = url.split(marker);
  return path || null;
};

const listByPublished = async (table, { includeUnpublished = false, orderBy = 'created_at', ascending = false } = {}) => {
  let query = requireSupabase().from(table).select('*').order(orderBy, { ascending });
  if (!includeUnpublished) {
    query = query.eq('published', true);
  }
  const { data, error } = await query;
  assertOk({ error });
  return data ?? [];
};

export const authAPI = {
  getSession: async () => {
    const { data, error } = await requireSupabase().auth.getSession();
    assertOk({ error });
    return data.session;
  },
  onAuthStateChange: (callback) => {
    if (!supabase) {
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
    return supabase.auth.onAuthStateChange(callback);
  },
  login: async ({ email, password }) => {
    const { data, error } = await requireSupabase().auth.signInWithPassword({ email, password });
    assertOk({ error });
    return data;
  },
  loginWithGoogle: async () => {
    const redirectPath = window.location.pathname === '/admin' ? '/admin' : '/admin/login';
    const redirectTo = `${window.location.origin}${redirectPath}`;
    const queryParams = {
      prompt: 'select_account',
    };
    const { data, error } = await requireSupabase().auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams,
      },
    });
    assertOk({ error });
    return data;
  },
  logout: async () => {
    const { error } = await requireSupabase().auth.signOut();
    assertOk({ error });
  },
};

export const uploadAPI = {
  uploadImage: async (file, type = 'record') => {
    const bucketMap = {
      record: 'records',
      certificate: 'certificates',
      blog: 'blog',
      site: 'site-assets',
    };
    const bucket = bucketMap[type] ?? 'records';
    const extension = file.name?.split('.').pop() || 'jpg';
    const path = `${Date.now()}-${crypto.randomUUID()}.${extension}`;
    const { error } = await requireSupabase().storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });
    assertOk({ error });
    return { filename: path, storagePath: path, path: buildPublicUrl(bucket, path) };
  },
  deleteImage: async (url, bucket = 'records') => {
    const path = storagePathFromPublicUrl(url, bucket);
    if (!path) return { message: 'No storage path detected' };
    const { error } = await requireSupabase().storage.from(bucket).remove([path]);
    assertOk({ error });
    return { message: 'Image deleted successfully' };
  },
  uploadApplicationAttachment: async (file) => {
    const extension = file.name?.split('.').pop() || 'bin';
    const path = `${Date.now()}-${crypto.randomUUID()}.${extension}`;
    const { error } = await requireSupabase()
      .storage
      .from('application-attachments')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || undefined,
      });
    assertOk({ error });
    return { filename: file.name, path };
  },
};

export const recordHoldersAPI = {
  getAll: async (options = {}) => {
    const rows = await listByPublished('record_holders', {
      ...options,
      orderBy: 'display_order',
      ascending: true,
    });
    return rows.map(normalizeRecordHolder);
  },
  create: async (holder) => {
    const payload = {
      name: holder.name,
      title: holder.title,
      images: holder.images ?? [],
      certificate_image: holder.certificate_image || null,
      achievement: holder.achievement || null,
      date: holder.date || null,
      display_order: holder.display_order ?? 0,
      published: holder.published ?? true,
    };
    const { data, error } = await requireSupabase()
      .from('record_holders')
      .insert(payload)
      .select()
      .single();
    assertOk({ error });
    return normalizeRecordHolder(data);
  },
  update: async (id, holder) => {
    const payload = {
      name: holder.name,
      title: holder.title,
      images: holder.images ?? [],
      certificate_image: holder.certificate_image || null,
      achievement: holder.achievement || null,
      date: holder.date || null,
      display_order: holder.display_order ?? 0,
      published: holder.published ?? true,
    };
    const { data, error } = await requireSupabase()
      .from('record_holders')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    assertOk({ error });
    return normalizeRecordHolder(data);
  },
  delete: async (id) => {
    const { error } = await requireSupabase().from('record_holders').delete().eq('id', id);
    assertOk({ error });
    return { message: 'Record holder deleted successfully' };
  },
};

export const applicationsAPI = {
  getAll: async () => {
    const { data, error } = await requireSupabase()
      .from('applications')
      .select('*')
      .order('created_at', { ascending: false });
    assertOk({ error });
    return Promise.all((data ?? []).map(normalizeApplication));
  },
  create: async (application) => {
    const { error } = await requireSupabase()
      .from('applications')
      .insert({
        name: application.name,
        email: application.email,
        phone: application.phone,
        message: application.message,
        record_type: application.record_type || null,
        attachment_paths: application.attachment_paths || [],
        attachment_names: application.attachment_names || [],
      });
    assertOk({ error });
    return { success: true };
  },
  updateStatus: async (id, status) => {
    const { data, error } = await requireSupabase()
      .from('applications')
      .update({ status })
      .eq('id', id)
      .select()
      .single();
    assertOk({ error });
    return data;
  },
};

export const siteConfigAPI = {
  get: async () => {
    if (!supabase) return DEFAULT_SITE_CONFIG;
    const { data, error } = await requireSupabase()
      .from('site_config')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    assertOk({ error });
    return data ?? DEFAULT_SITE_CONFIG;
  },
  update: async (config) => {
    const payload = {
      email: config.email,
      whatsapp: config.whatsapp,
      about_content: config.about_content ?? [],
      hero_title: config.hero_title,
      hero_subtitle: config.hero_subtitle,
      hero_description: config.hero_description,
      updated_at: new Date().toISOString(),
    };

    const readMatchingSavedConfig = async () => {
      const savedConfig = await siteConfigAPI.get();
      if (siteConfigMatchesPayload(savedConfig, payload)) {
        return savedConfig;
      }
      return null;
    };

    const verifySaved = async () => {
      const savedConfig = await readMatchingSavedConfig();
      if (savedConfig) return savedConfig;
      return null;
    };

    const runWrite = async (writeOperation) => {
      try {
        const { error } = await writeOperation();
        if (error) throw new Error(parseErrorMessage(error));
      } catch (error) {
        if (!isBodyStreamReadError(error)) throw error;
      }
    };

    if (config.id) {
      await runWrite(() =>
        requireSupabase()
          .from('site_config')
          .update(payload)
          .eq('id', config.id)
      );
      const updatedConfig = await verifySaved();
      if (updatedConfig) return updatedConfig;
    }

    await runWrite(() => requireSupabase().from('site_config').insert(payload));
    const insertedConfig = await verifySaved();
    if (insertedConfig) return insertedConfig;

    const sessionDescription = await describeCurrentSession();
    throw new Error(
      `Settings were not saved after update and insert attempts. ${sessionDescription} Run supabase/any_logged_in_admin.sql in the live Supabase SQL editor, then sign out and sign in again.`
    );
  },
};

export const faqAPI = {
  getAll: async (options = {}) => {
    const rows = await listByPublished('faqs', {
      ...options,
      orderBy: 'display_order',
      ascending: true,
    });
    return rows.map(normalizeFaq);
  },
  create: async (faq) => {
    const { data, error } = await requireSupabase()
      .from('faqs')
      .insert({
        question: faq.question,
        answer: faq.answer,
        display_order: faq.order ?? faq.display_order ?? 0,
        published: faq.published ?? true,
      })
      .select()
      .single();
    assertOk({ error });
    return normalizeFaq(data);
  },
  update: async (id, faq) => {
    const { data, error } = await requireSupabase()
      .from('faqs')
      .update({
        question: faq.question,
        answer: faq.answer,
        display_order: faq.order ?? faq.display_order ?? 0,
        published: faq.published ?? true,
      })
      .eq('id', id)
      .select()
      .single();
    assertOk({ error });
    return normalizeFaq(data);
  },
  delete: async (id) => {
    const { error } = await requireSupabase().from('faqs').delete().eq('id', id);
    assertOk({ error });
    return { message: 'FAQ deleted successfully' };
  },
};

export const blogAPI = {
  getAll: async (options = {}) => {
    const rows = await listByPublished('blog_posts', options);
    return rows.map(normalizeBlogPost);
  },
  getOne: async (id) => {
    const { data, error } = await requireSupabase().from('blog_posts').select('*').eq('id', id).single();
    assertOk({ error });
    return normalizeBlogPost(data);
  },
  create: async (post) => {
    const { data, error } = await requireSupabase()
      .from('blog_posts')
      .insert({
        title: post.title,
        slug: post.slug || slugify(post.title),
        excerpt: post.excerpt,
        body: post.content ?? post.body,
        cover_image: post.image ?? post.cover_image,
        author: post.author || 'Admin',
        published: post.published ?? true,
      })
      .select()
      .single();
    assertOk({ error });
    return normalizeBlogPost(data);
  },
  update: async (id, post) => {
    const { data, error } = await requireSupabase()
      .from('blog_posts')
      .update({
        title: post.title,
        slug: post.slug || slugify(post.title),
        excerpt: post.excerpt,
        body: post.content ?? post.body,
        cover_image: post.image ?? post.cover_image,
        author: post.author || 'Admin',
        published: post.published ?? true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    assertOk({ error });
    return normalizeBlogPost(data);
  },
  delete: async (id) => {
    const { error } = await requireSupabase().from('blog_posts').delete().eq('id', id);
    assertOk({ error });
    return { message: 'Blog post deleted successfully' };
  },
};

export default supabase;
